/*
  Shared video behaviour (site-behaviour-spec.md > Video), used by the product gallery and the
  tech story sections.
  - Muted, inline, plays while on screen, pauses off screen. Loops loop; "Play once and hold" stops
    on its last frame and doesn't restart. Desktop-only clips show their still on mobile.
    Phones get the 960 x 540 cut when there is one. Reduced motion: stills only.
  Sound (clips with has_audio only; silent clips get no controls):
  - Autoplay always starts muted, unless the visitor has already interacted with the page
    (thumbnail, arrow, swipe, full screen, or any tap the browser counts) and hasn't turned sound off.
  - Muted with audio: a "Tap for sound" pill shows on the video. The 44 px toggle is always there.
  - The visitor's choice (on/off) is remembered for the session and applies to every clip.
*/
window.BBSound = window.BBSound || (() => {
  const KEY = 'bb-video-sound';
  let interacted = false;
  const players = new Set();

  const choice = () => { try { return sessionStorage.getItem(KEY); } catch (e) { return null; } };
  const store = (value) => { try { sessionStorage.setItem(KEY, value); } catch (e) { /* private mode */ } };
  const activated = () => interacted || Boolean(navigator.userActivation?.hasBeenActive);
  const wantsSound = () => choice() !== 'off' && (choice() === 'on' || activated());

  function updateUI(root, video) {
    const on = !video.muted;
    const toggle = root.querySelector('[data-sound-toggle]');
    const pill = root.querySelector('[data-sound-pill]');
    if (toggle) {
      toggle.setAttribute('aria-pressed', String(on));
      toggle.setAttribute('aria-label', on ? toggle.dataset.labelOff : toggle.dataset.labelOn);
      toggle.classList.toggle('is-on', on);
    }
    if (pill) pill.hidden = on || choice() === 'off';
  }

  return {
    markInteracted() { interacted = true; },

    /* Play a clip. With audio it tries sound when allowed, and falls back to muted if the browser says no */
    play(video) {
      const audio = 'audio' in video.dataset;
      if (!audio) {
        video.muted = true;
        return video.play().catch(() => {});
      }
      video.muted = !wantsSound();
      return video.play().catch(() => {
        if (video.muted) return;
        video.muted = true;
        return video.play().catch(() => {});
      });
    },

    /* Wire the pill and toggle inside root to its video */
    bind(root, video) {
      if (!('audio' in video.dataset) || root.dataset.soundBound) return;
      root.dataset.soundBound = 'true';
      const entry = { root, video };
      players.add(entry);
      const set = (on) => {
        interacted = true;
        store(on ? 'on' : 'off');
        players.forEach((p) => {
          if (p.video === video) {
            p.video.muted = !on;
            if (on && p.video.paused) p.video.play().catch(() => {});
          } else if (!on) {
            p.video.muted = true;
          }
          updateUI(p.root, p.video);
        });
      };
      root.querySelector('[data-sound-toggle]')?.addEventListener('click', (e) => {
        e.stopPropagation();
        set(video.muted);
      });
      root.querySelector('[data-sound-pill]')?.addEventListener('click', (e) => {
        e.stopPropagation();
        set(true);
      });
      video.addEventListener('volumechange', () => updateUI(root, video));
      updateUI(root, video);
    },
  };
})();

if (!customElements.get('video-player')) {
class VideoPlayer extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;
    this.mobile = window.matchMedia('(max-width: 989px)');
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.playOnce = !('loop' in this.dataset);
    this.video.loop = !this.playOnce;
    this.video.addEventListener('ended', () => { this.finished = true; });
    this.setSource();
    this.mobile.addEventListener('change', () => { this.setSource(); this.update(); });
    window.BBSound.bind(this, this.video);
    new IntersectionObserver((entries) => {
      this.visible = entries[0].isIntersecting;
      this.update();
    }, { threshold: 0.35 }).observe(this);
  }

  setSource() {
    const v = this.video;
    if (!v.dataset.desktopSrc) v.dataset.desktopSrc = v.dataset.src;
    const want = this.mobile.matches && v.dataset.mobileSrc ? v.dataset.mobileSrc : v.dataset.desktopSrc;
    if (this.allowed() && v.getAttribute('src') !== want) v.setAttribute('src', want);
  }

  allowed() {
    return !this.reduced.matches && !(this.mobile.matches && 'desktopOnly' in this.dataset);
  }

  update() {
    if (this.visible && this.allowed() && !this.finished) {
      if (!this.video.getAttribute('src')) this.setSource();
      window.BBSound.play(this.video);
    } else {
      this.video.pause();
    }
  }
}

customElements.define('video-player', VideoPlayer);
}
